import {
  Component,
  DestroyRef,
  HostBinding,
  Input,
  OnInit,
  Optional,
  SkipSelf,
  forwardRef
} from "@angular/core";
import {
  ControlContainer,
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { v4 } from "uuid";

@Component({
  selector: "app-input",
  templateUrl: "./input.component.html",
  styleUrls: ["./input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputComponent)
    }
  ]
})
export class InputComponent implements ControlValueAccessor, OnInit {
  @Input() type = "";
  @Input() mask = "";
  @Input() formControlName = "";
  @Input() label = "";
  @Input() id = v4();
  @Input() value = "";
  @Input() placeholder = "";
  @Input() autocomplete = "";
  @Input() validation = false;
  @Input() dropSpecialCharacters = true;
  @Input() prefix = "";
  @Input() thousandSeparator = ".";
  @Input() decimalMarker: "." | "," = ",";
  @Input() maxlength = this.type === "textarea" ? 500 : 255;
  @Input() hint = "";
  @Input() invalidMessage = "";
  @Input() isValid?: (value: string) => boolean;

  @HostBinding("style.opacity")
  get opacity() {
    return this.input?.disabled ? 0.25 : 1;
  }

  onChange?: () => void;
  onTouch?: () => void;
  disabled = false;
  showMaxLengthWarning = false;
  private maxLengthTimeout?: ReturnType<typeof setTimeout>;

  get input() {
    return this.controlContainer?.control?.get(this.formControlName);
  }

  control: FormGroup = new FormGroup({});

  constructor(
    @Optional() @SkipSelf() public controlContainer: ControlContainer,
    destroyRef: DestroyRef
  ) {
    destroyRef.onDestroy(() => {
      if (this.maxLengthTimeout) clearTimeout(this.maxLengthTimeout);
    });
  }

  ngOnInit(): void {
    const control = this.controlContainer?.control as FormGroup | null;
    if (!control) return;
    this.control = control;
  }

  writeValue(value: string): void {
    this.value = value ? value : "";
  }
  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
  // Chamado pelo Angular quando o próprio FormControl é habilitado ou
  // desabilitado (registerOnDisabledChange). O <input> interno já está
  // ligado ao mesmo control via [formControlName], então não há nada a fazer
  // aqui — e chamar enable()/disable() de volta dispara este callback de
  // novo, gerando "Maximum call stack size exceeded".
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = inputElement.value ?? "";
    const currentLength = value.length;

    if (currentLength >= this.maxlength) {
      this.triggerMaxLengthWarning();
    }

    if (this.input && this.isValid) {
      const ok = this.isValid(value);

      if (!ok) {
        this.input.setErrors({
          ...(this.input.errors ?? {}),
          customInvalid: true
        });
      } else {
        const errors = { ...(this.input.errors ?? {}) };
        delete errors["customInvalid"];
        const nextErrors = Object.keys(errors).length ? errors : null;
        this.input.setErrors(nextErrors);
      }
    }
  }

  triggerMaxLengthWarning(): void {
    this.showMaxLengthWarning = true;

    if (this.maxLengthTimeout) {
      clearTimeout(this.maxLengthTimeout);
    }

    this.maxLengthTimeout = setTimeout(() => {
      this.showMaxLengthWarning = false;
    }, 3000);
  }
}
